/**
 * Race Feature Type Definitions
 *
 * Types for the multiplayer racing feature including room configuration,
 * participants, leaderboard, and realtime events.
 */

import { TOKO_APPS } from '@/config/apps';

// ============================================================
// Constants
// ============================================================

export const RACE_STATES = {
  WAITING: 'waiting',
  RACING: 'racing',
  FINISHED: 'finished',
} as const;

export type RaceState = (typeof RACE_STATES)[keyof typeof RACE_STATES];

export const PARTICIPANT_STATUS = {
  JOINED: 'joined',
  READY: 'ready',
  RACING: 'racing',
  FINISHED: 'finished',
} as const;

export type ParticipantStatus = (typeof PARTICIPANT_STATUS)[keyof typeof PARTICIPANT_STATUS];

// ============================================================
// Database Row Types
// ============================================================

export interface RaceRoom {
  id: string;
  created_at: string;
  created_by: string;
  join_code: string;
  state: RaceState;
  race_start_time: string | null;
  finished_at: string | null;
  allowed_apps: string[];
  max_participants: number;
  question_count: number;
}

export interface RaceParticipant {
  id: string;
  race_room_id: string;
  user_id: string;
  username: string;
  app_name: string;
  status: ParticipantStatus;
  joined_at: string;
  total_cards: number;
  correct_count: number;
  incorrect_count: number;
  total_time: number | null;
  finished_at: string | null;
}

export interface RaceProgress {
  id: string;
  race_room_id: string;
  user_id: string;
  card_number: number;
  is_correct: boolean;
  time_taken: number;
  timestamp: string;
}

export interface RaceLiveStats {
  id: string;
  race_room_id: string;
  user_id: string;
  cards_completed: number;
  correct_count: number;
  incorrect_count: number;
  accuracy: number;
  total_time: number;
  last_updated: string;
}

// ============================================================
// Configuration Types
// ============================================================

export interface RaceConfig {
  allowedApps: string[];
  maxParticipants: number;
  questionCount: number;
}

export const DEFAULT_RACE_CONFIG: RaceConfig = {
  allowedApps: TOKO_APPS.filter(app => app.id !== 'addition-plus').map(app => app.id),
  maxParticipants: 100,
  questionCount: 20,
};

// ============================================================
// Realtime Event Types
// ============================================================

export interface StartRacePayload {
  startTime: string;
  questionCount: number;
}

export interface EndRacePayload {
  endTime: string;
  reason: 'host_ended' | 'all_finished' | 'timeout';
}

export interface PresenceState {
  [key: string]: Array<{
    user_id: string;
    username: string;
    app_name: string;
    status: ParticipantStatus;
  }>;
}

// ============================================================
// Leaderboard Types
// ============================================================

export interface LeaderboardEntry {
  rank: number;
  participant: RaceParticipant;
  cardsCompleted: number;
  correctCount: number;
  accuracy: number;
  avgTimePerCard: number;
  totalTime: number;
  isFinished: boolean;
}

// ============================================================
// Host Page State Types
// ============================================================

export type RaceHostPhase =
  | 'configure'
  | 'waiting'
  | 'countdown'
  | 'racing'
  | 'results';

export interface RaceHostState {
  phase: RaceHostPhase;
  room: RaceRoom | null;
  participants: RaceParticipant[];
  leaderboard: LeaderboardEntry[];
  countdown: number | null;
  error: string | null;
}
